import { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  TextField,
  Autocomplete,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { getMomentTz } from "@dexteel/mesf-core";
import type { Moment } from "moment";
import { TaskDateSection } from "../../../shared/components/modals/TaskDateSection";

const moment = getMomentTz();

// ============================================
// Types
// ============================================

interface ShellOption {
  partId: number;
  name: string;
  /** Pre-computed last task timestamp to avoid API call on selection */
  lastTaskTimestamp?: Date | null;
}

interface FormData {
  timestamp: Moment | null;
  selectedShell: ShellOption | null;
  operatorNumber: string;
}

interface Props {
  open: boolean;
  isLoading: boolean;
  /** Slot name to display (Upper Location / Lower Location) */
  slotName: string;
  /** PartId of the slot the shell will be installed on */
  slotPartId: number;
  /** Available shells in Shell Spares filtered by matching shell type */
  availableShells: ShellOption[];
  /** Last task timestamp on this slot (when a previous shell was uninstalled) */
  locationLastTimestamp?: Date | null;
  /** PartId of the shell currently installed on the slot (swap scenario) */
  occupantPartId?: number | null;
  onClose: () => void;
  onConfirm: (
    slotPartId: number,
    shellPartId: number,
    operatorNumber: number,
    timestamp: Date | null,
  ) => void;
}

// ============================================
// Component
// ============================================

export const SetShellOnlineModal = ({
  open,
  isLoading,
  slotName,
  slotPartId,
  availableShells,
  locationLastTimestamp,
  occupantPartId,
  onClose,
  onConfirm,
}: Props) => {
  const { control, handleSubmit, reset, watch, trigger } = useForm<FormData>({
    defaultValues: {
      timestamp: null,
      selectedShell: null,
      operatorNumber: "",
    },
    mode: "onChange",
  });

  const selectedShell = watch("selectedShell");

  useEffect(() => {
    if (open) {
      reset({
        timestamp: moment(),
        selectedShell: null,
        operatorNumber: "",
      });
    }
  }, [open, reset]);

  const onSubmit = (data: FormData) => {
    const operatorNumber = parseInt(data.operatorNumber, 10);
    const ts = data.timestamp?.toDate() ?? null;
    const shellPartId = data.selectedShell?.partId;
    if (!shellPartId) return;
    onConfirm(slotPartId, shellPartId, operatorNumber, ts);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set Shell Online - Confirmation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {/* Slot context (read-only) */}
          <Box>
            <Typography variant="body2" color="text.secondary">
              Slot
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {slotName}
            </Typography>
          </Box>

          {/* Shell selector */}
          <Controller
            name="selectedShell"
            control={control}
            rules={{ required: "Select a shell to install" }}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                options={availableShells}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option.partId === value.partId
                }
                onChange={(_, value) => field.onChange(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Shell"
                    size="small"
                    error={!!error}
                    helperText={
                      error?.message || "Choose a shell from Shell Spares"
                    }
                  />
                )}
                noOptionsText="No shells available in Shell Spares"
              />
            )}
          />

          <Typography variant="body2" color="text.secondary">
            This will install the shell on the selected slot and set it as
            online.
          </Typography>

          {/* Date selection with task history + location floor */}
          <TaskDateSection
            control={control}
            trigger={trigger}
            partId={selectedShell?.partId ?? null}
            open={open}
            action="install"
            installedPartLastTimestamp={selectedShell?.lastTaskTimestamp}
            locationLastTimestamp={locationLastTimestamp}
            occupantPartId={occupantPartId}
          />

          <Controller
            name="operatorNumber"
            control={control}
            rules={{ required: "Operator number is required" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Operator Number"
                size="small"
                fullWidth
                error={!!error}
                helperText={error?.message || "Enter your Operator Number"}
                inputProps={{ inputMode: "numeric" }}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  field.onChange(value);
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="success"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size="1rem" /> : undefined}
        >
          Set Online
        </Button>
      </DialogActions>
    </Dialog>
  );
};
